import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import {
  FileText, Save, Send, Download, Printer, Mail, Clock,
  ArrowLeft, CheckCircle2, XCircle, Upload, Trash2, AlertCircle, Eye,
  RotateCcw, Shield
} from 'lucide-react';
import { DocumentViewer } from '../components/DocumentViewer';

function EstadoBadge({ estado }) {
  const map = {
    firmado: { label: 'Firmado', cls: 'badge-firmado' },
    pendiente_firma: { label: 'Pdte. firma', cls: 'badge-pendiente_firma' },
    borrador: { label: 'Borrador', cls: 'badge-borrador' },
    rechazado: { label: 'Rechazado', cls: 'badge-rechazado' },
    archivado: { label: 'Archivado', cls: 'badge-archivado' },
  };
  const m = map[estado] || { label: estado, cls: '' };
  return <span className={`badge ${m.cls}`}>{m.label}</span>;
}

export default function PntDetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canSign, user, isAdmin } = useAuth();
  const isNew = id === 'nuevo';

  const [pnt, setPnt] = useState(null);
  const [form, setForm] = useState({
    codigo: '', titulo: '', version: '01', categoria_id: '', responsable_id: '',
    descripcion: '', objetivo: '', alcance: '', metodo: '', notas: '',
    redactado_por: '', aprobado_por: '', fecha_proxima_revision: ''
  });
  const [categorias, setCategorias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [visor, setVisor] = useState(null);
  const [modalResetEstado, setModalResetEstado] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('borrador');
  const [modalEliminar, setModalEliminar] = useState(false);
  const [confirmCodigo, setConfirmCodigo] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [eliminando, setEliminando] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/pnts/categorias').then(r => setCategorias(r.data));
    api.get('/usuarios').then(r => setUsuarios(r.data)).catch(() => {});
    if (!isNew) {
      api.get(`/pnts/${id}`).then(r => {
        setPnt(r.data);
        setForm({
          codigo: r.data.codigo || '',
          titulo: r.data.titulo || '',
          version: r.data.version || '01',
          categoria_id: r.data.categoria_id || '',
          responsable_id: r.data.responsable_id || '',
          descripcion: r.data.descripcion || '',
          objetivo: r.data.objetivo || '',
          alcance: r.data.alcance || '',
          metodo: r.data.metodo || '',
          notas: r.data.notas || '',
          redactado_por: r.data.redactado_por || '',
          aprobado_por: r.data.aprobado_por || '',
          fecha_proxima_revision: r.data.fecha_proxima_revision || ''
        });
      }).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const handleSave = async () => {
    if (!form.titulo || !form.categoria_id) {
      setError('Título y categoría son obligatorios');
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      if (isNew) {
        const res = await api.post('/pnts', form);
        setSuccess('PNT creado correctamente');
        setTimeout(() => navigate(`/pnts/${res.data.id}`), 1000);
      } else {
        await api.put(`/pnts/${id}`, form);
        setSuccess('PNT guardado correctamente');
        const r = await api.get(`/pnts/${id}`);
        setPnt(r.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handlePasarPdteFirma = async () => {
    if (!window.confirm('¿Marcar este documento como "Pendiente de firma"?\n\nDespués podrás enviar la solicitud a la Directora Técnica desde el módulo de Firmas.')) return;
    try {
      await api.put(`/pnts/${id}/reset-estado`, { nuevo_estado: 'pendiente_firma' });
      setSuccess('Documento marcado como "Pendiente de firma". Ahora ve al módulo de Firmas para enviar la solicitud.');
      const r = await api.get(`/pnts/${id}`);
      setPnt(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar el estado');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('seccion', 'pnts');
    formData.append('entidad_tipo', 'pnt');
    formData.append('entidad_id', id);
    setUploadingFile(true);
    try {
      await api.post('/archivos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await api.get(`/pnts/${id}`);
      setPnt(r.data);
      setSuccess('Archivo subido correctamente');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al subir archivo');
    } finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleDeleteFile = async (archivoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    await api.delete(`/archivos/${archivoId}`);
    const r = await api.get(`/pnts/${id}`);
    setPnt(r.data);
  };

  const handleResetEstado = async () => {
    try {
      const r = await api.put(`/pnts/${id}/reset-estado`, { nuevo_estado: nuevoEstado });
      setPnt(r.data.pnt);
      setSuccess(r.data.mensaje);
      setModalResetEstado(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar estado');
      setModalResetEstado(false);
    }
  };

  const handleEliminar = async () => {
    if (!confirmCodigo.trim() || !confirmPassword.trim()) {
      setError('Debes escribir el código del PNT y tu contraseña de administrador.');
      return;
    }
    setEliminando(true);
    setError('');
    try {
      const r = await api.delete(`/pnts/${id}`, {
        data: {
          confirmacion_codigo: confirmCodigo,
          password: confirmPassword
        }
      });
      setSuccess(r.data.mensaje);
      setModalEliminar(false);
      setConfirmCodigo('');
      setConfirmPassword('');
      setTimeout(() => navigate('/pnts'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar el PNT');
    } finally {
      setEliminando(false);
    }
  };

  const isFirmado = pnt?.estado === 'firmado';
  const canEditNow = canEdit() && !isFirmado;

  if (loading) return <div className="loading-overlay"><div className="spinner" /><span>Cargando PNT...</span></div>;

  return (
    <div>
      {/* Breadcrumb + acciones */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/pnts')}>
            <ArrowLeft size={16} /> Volver
          </button>
          <div>
            <h1 style={{ fontSize: '1.25rem' }}>
              {isNew ? 'Nuevo PNT' : form.titulo || 'Detalle PNT'}
            </h1>
            {pnt && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: 700 }}>{pnt.codigo}</span>
                <span style={{ color: 'var(--color-text-3)', fontSize: '0.8rem' }}>v{pnt.version}</span>
                <EstadoBadge estado={pnt.estado} />
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isNew && pnt?.estado === 'borrador' && canEdit() && (
            <button className="btn btn-outline" onClick={handlePasarPdteFirma}
              style={{ borderColor: 'var(--color-warning)', color: 'var(--color-warning)' }}>
              <Send size={15} /> Pasar a Pdte. firma
            </button>
          )}
          {!isNew && isAdmin() && pnt && (
            <button className="btn btn-outline btn-sm" onClick={() => setModalResetEstado(true)}
              style={{ borderColor: 'var(--color-warning)', color: 'var(--color-warning)' }}>
              <RotateCcw size={14} /> Cambiar estado
            </button>
          )}
          {!isNew && isAdmin() && pnt && (
            <button className="btn btn-outline btn-sm" onClick={() => setModalEliminar(true)}
              style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}>
              <Trash2 size={14} /> Eliminar PNT
            </button>
          )}
          {!isNew && (
            <button className="btn btn-outline btn-sm" onClick={() => window.open(`/api/informes/pnts?categoria_id=${pnt?.categoria_id}`, '_blank')}>
              <Download size={15} /> PDF
            </button>
          )}
          {canEditNow && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <div className="spinner" style={{ width: 15, height: 15 }} /> : <Save size={15} />}
              Guardar
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}><AlertCircle size={16} />{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} />{success}</div>}
      {isFirmado && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <CheckCircle2 size={16} />
          <div>
            <strong>Documento firmado y bloqueado.</strong> Para modificarlo, crea una nueva versión.
            {pnt.fecha_aprobacion && ` Firmado el ${new Date(pnt.fecha_aprobacion).toLocaleDateString('es-ES')}.`}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        {/* Formulario principal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Identificación */}
          <div className="card">
            <div className="card-header">
              <h3>Datos de Identificación</h3>
            </div>
            <div className="card-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Código <span className="required">*</span></label>
                  <input className="form-control" value={form.codigo} onChange={e => set('codigo', e.target.value)} placeholder="Ej: G001" disabled={!canEditNow} style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Versión</label>
                  <input className="form-control" value={form.version} onChange={e => set('version', e.target.value)} placeholder="01" disabled={!canEditNow} style={{ fontFamily: 'var(--font-mono)' }} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Título <span className="required">*</span></label>
                <input className="form-control" value={form.titulo} onChange={e => set('titulo', e.target.value)} placeholder="Título del procedimiento..." disabled={!canEditNow} style={{ fontWeight: 600, fontSize: '1rem' }} />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Categoría <span className="required">*</span></label>
                  <select className="form-control" value={form.categoria_id} onChange={e => set('categoria_id', e.target.value)} disabled={!canEditNow}>
                    <option value="">— Seleccionar —</option>
                    {categorias.map(c => <option key={c.id} value={c.id}>{c.codigo} - {c.nombre}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Próxima Revisión</label>
                  <input type="date" className="form-control" value={form.fecha_proxima_revision} onChange={e => set('fecha_proxima_revision', e.target.value)} disabled={!canEditNow} />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Redactado por</label>
                  <input className="form-control" value={form.redactado_por} onChange={e => set('redactado_por', e.target.value)} placeholder="Alaitz Goenaga" disabled={!canEditNow} />
                </div>
                <div className="form-group">
                  <label className="form-label">Aprobado por</label>
                  <input className="form-control" value={form.aprobado_por} onChange={e => set('aprobado_por', e.target.value)} placeholder="Fernando Ayllón" disabled={!canEditNow} />
                </div>
              </div>
            </div>
          </div>

          {/* Contenido del procedimiento */}
          <div className="card">
            <div className="card-header">
              <h3>Contenido del Procedimiento</h3>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">1. Objetivo</label>
                <textarea className="form-control" rows={4} value={form.objetivo} onChange={e => set('objetivo', e.target.value)} placeholder="Describe el objetivo de este procedimiento..." disabled={!canEditNow} />
              </div>
              <div className="form-group">
                <label className="form-label">2. Alcance</label>
                <textarea className="form-control" rows={4} value={form.alcance} onChange={e => set('alcance', e.target.value)} placeholder="Define el alcance y ámbito de aplicación..." disabled={!canEditNow} />
              </div>
              <div className="form-group">
                <label className="form-label">3. Método / Responsabilidades</label>
                <textarea className="form-control" rows={8} value={form.metodo} onChange={e => set('metodo', e.target.value)} placeholder="Describe el método, pasos e instrucciones..." disabled={!canEditNow} />
              </div>
              <div className="form-group">
                <label className="form-label">Notas y Observaciones</label>
                <textarea className="form-control" rows={3} value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Notas adicionales..." disabled={!canEditNow} />
              </div>
            </div>
          </div>

          {/* Historial de firmas */}
          {!isNew && pnt?.solicitudes_firma?.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Clock size={16} color="var(--color-primary)" /> Historial de Firma
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {pnt.solicitudes_firma.map((sf, i) => (
                  <div key={i} style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-gray-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {sf.estado === 'firmado' ? '✅ Firmado' : sf.estado === 'rechazado' ? '❌ Rechazado' : '⏳ Pendiente'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-3)', marginTop: 2 }}>
                        Solicitado por: {sf.solicitante_nombre} · {new Date(sf.created_at).toLocaleString('es-ES')}
                      </div>
                      {sf.nombre_firmante && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-success)', marginTop: 2 }}>
                          Firmado por: {sf.nombre_firmante}, {sf.cargo}
                        </div>
                      )}
                      {sf.comentario_rechazo && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)', marginTop: 2 }}>
                          Motivo rechazo: {sf.comentario_rechazo}
                        </div>
                      )}
                    </div>
                    <EstadoBadge estado={sf.estado} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Panel lateral */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Estado */}
          {!isNew && pnt && (
            <div className="card">
              <div className="card-header"><h3>Estado del Documento</h3></div>
              <div className="card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>Estado actual</span>
                    <EstadoBadge estado={pnt.estado} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>Creado</span>
                    <span style={{ fontSize: '0.82rem' }}>{pnt.fecha_creacion ? new Date(pnt.fecha_creacion).toLocaleDateString('es-ES') : '—'}</span>
                  </div>
                  {pnt.fecha_aprobacion && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>Aprobado</span>
                      <span style={{ fontSize: '0.82rem', color: 'var(--color-success)', fontWeight: 600 }}>{new Date(pnt.fecha_aprobacion).toLocaleDateString('es-ES')}</span>
                    </div>
                  )}
                  {pnt.fecha_proxima_revision && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>Próx. revisión</span>
                      <span style={{ fontSize: '0.82rem', color: 'var(--color-warning)', fontWeight: 600 }}>{new Date(pnt.fecha_proxima_revision).toLocaleDateString('es-ES')}</span>
                    </div>
                  )}
                  {pnt.categoria_nombre && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>Categoría</span>
                      <span style={{ fontSize: '0.82rem', color: pnt.color, fontWeight: 600 }}>{pnt.categoria_nombre}</span>
                    </div>
                  )}
                </div>

                {/* Bloque firma si está firmado */}
                {isFirmado && pnt.solicitudes_firma?.find(sf => sf.estado === 'firmado') && (
                  <div style={{ marginTop: 16, border: '2px solid var(--color-success)', borderRadius: 8, padding: 12, background: 'var(--color-success-bg)' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-success)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      ✅ Firma Electrónica
                    </div>
                    <div style={{ fontSize: '0.78rem', lineHeight: 1.7, color: 'var(--color-text)' }}>
                      <div><strong>Firmado por:</strong> {pnt.solicitudes_firma.find(sf => sf.estado === 'firmado')?.nombre_firmante}</div>
                      <div><strong>Cargo:</strong> Directora Técnica</div>
                      <div><strong>Empresa:</strong> LOMHIFAR S.L.</div>
                      <div><strong>Fecha:</strong> {pnt.fecha_aprobacion ? new Date(pnt.fecha_aprobacion).toLocaleDateString('es-ES') : '—'}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Archivos adjuntos */}
          {!isNew && (
            <div className="card">
              <div className="card-header">
                <h3>Documentos Adjuntos</h3>
                {canEdit() && (
                  <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                    {uploadingFile ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <Upload size={14} />}
                    Subir
                    <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
                  </label>
                )}
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {!pnt?.archivos?.length ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-3)', fontSize: '0.85rem' }}>
                    Sin archivos adjuntos
                  </div>
                ) : (
                  pnt.archivos.map(a => (
                    <div key={a.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-gray-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.nombre_original}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--color-text-3)' }}>
                          {a.subido_por_nombre} · {new Date(a.created_at).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Previsualizar" onClick={() => setVisor(a)}>
                          <Eye size={13} />
                        </button>
                        {user?.rol === 'administrador' && (
                          <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--color-danger)' }} onClick={() => handleDeleteFile(a.id)} title="Eliminar">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Visor de documentos */}
      {visor && <DocumentViewer archivo={visor} onClose={() => setVisor(null)} />}

      {/* Modal reset estado */}
      {modalResetEstado && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Shield size={18} color="var(--color-warning)" />
                Cambiar estado del PNT
              </h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setModalResetEstado(false)}>
                <XCircle size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning" style={{ marginBottom: 20 }}>
                <AlertCircle size={16} />
                <div>
                  <strong>Acción de administrador</strong> — Estás a punto de cambiar el estado del documento
                  <strong> {pnt?.codigo} · {pnt?.titulo}</strong>.
                  El estado actual es <strong>{pnt?.estado}</strong>.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Nuevo estado <span className="required">*</span></label>
                <select className="form-control" value={nuevoEstado} onChange={e => setNuevoEstado(e.target.value)}>
                  <option value="borrador">📝 Borrador</option>
                  <option value="pendiente_firma">⏳ Pendiente de firma</option>
                  <option value="en_revision">🔍 En revisión</option>
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 6 }}>
                  ⚠️ No se puede cambiar a "Firmado" — ese estado solo lo otorga la firma electrónica.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModalResetEstado(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleResetEstado}
                style={{ background: 'var(--color-warning)', borderColor: 'var(--color-warning)' }}>
                <RotateCcw size={15} /> Confirmar cambio de estado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal eliminar PNT (doble confirmación) */}
      {modalEliminar && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Trash2 size={18} color="var(--color-danger)" />
                Eliminar PNT
              </h2>
              <button className="btn btn-ghost btn-icon" onClick={() => { setModalEliminar(false); setConfirmCodigo(''); setConfirmPassword(''); setError(''); }}>
                <XCircle size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger" style={{ marginBottom: 20 }}>
                <AlertCircle size={16} />
                <div>
                  <strong>⚠️ Acción crítica de administrador</strong>
                  <p style={{ margin: '8px 0 0', fontSize: '0.85rem' }}>
                    Estás a punto de eliminar el PNT <strong>{pnt?.codigo} · {pnt?.titulo}</strong>.
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--color-text-2)' }}>
                    El PNT no desaparecerá de la base de datos, sino que se marcará como eliminado y podrá restaurarse desde la papelera.
                  </p>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  1️⃣ Escribe el código del PNT para confirmar <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  value={confirmCodigo}
                  onChange={e => setConfirmCodigo(e.target.value)}
                  placeholder={pnt?.codigo || ''}
                  style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.05em' }}
                  autoFocus
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 6 }}>
                  Debe coincidir exactamente con <strong style={{ fontFamily: 'var(--font-mono)' }}>{pnt?.codigo}</strong>
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">
                  2️⃣ Tu contraseña de administrador <span className="required">*</span>
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Contraseña"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setModalEliminar(false); setConfirmCodigo(''); setConfirmPassword(''); setError(''); }}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEliminar}
                disabled={eliminando || !confirmCodigo || !confirmPassword}
                style={{ background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                {eliminando ? <div className="spinner" style={{ width: 15, height: 15 }} /> : <Trash2 size={15} />}
                Eliminar definitivamente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
